/**
 * BRD-CLIN-2026-006 §8.2 — whether this patient may be titrated under Welby's standing orders.
 * -----------------------------------------------------------------------------------------
 * CLIENT-SIDE MIRROR of `ClinicalEligibilityView` in welby-micro-service
 * (`src/core/patient/clinical-eligibility/models/clinical-eligibility.model.ts`, PR #972). The two
 * repos share no package, so the shape is duplicated. Field names are the API's, snake_case, not
 * renamed on the way in — a rename here is a silent break the compiler cannot see.
 *
 * READ-ONLY IN THIS REPO TODAY. The screening itself is an attestation a clinician records, and the
 * guide fields that collect it are not seeded yet, so nothing here POSTs. What this powers is the
 * §8.2 gate being VISIBLE on the call: NFR-5.3 requires the system not to present dose escalation
 * beyond the LVAD / ESRD / eGFR<30 exclusions, and a clinician cannot honour a gate they cannot see.
 *
 * `Timestamp` fields arrive as JSON, not as Firestore Timestamps — the API serialises them — so they
 * are typed as `unknown` rather than pretending to a shape this repo never verifies. Nothing here
 * reads them yet; when something does, it must parse defensively.
 */
export enum EligibilityStatus {
  ELIGIBLE = 'eligible',
  EXCLUDED = 'excluded',
  /**
   * No clinician has screened this patient. TREATED EXACTLY AS `EXCLUDED` by every consumer, and
   * that is the whole point: absence of a decision is not permission. It is also the state every
   * patient is in until someone screens them, so it must never read as "fine".
   */
  NOT_SCREENED = 'not_screened',
}

export enum ExclusionReason {
  LVAD = 'lvad',
  ESRD = 'esrd',
  EGFR_LT_30 = 'egfr_lt_30',
}

export enum EligibilitySource {
  /** A named clinician answered the screening questions. The only source that may grant eligibility. */
  ATTESTATION = 'attestation',
  /** Derived from ICD codes on the chart. A one-way ratchet: it may only ever exclude. */
  ICD_INFERENCE = 'icd_inference',
}

export interface ClinicalEligibility {
  status: EligibilityStatus;
  reasons: ExclusionReason[];
  source: EligibilitySource;
  egfr_value: number | null;
  icd_codes_seen: string[];
  screened_by: string | null;
  screened_at: unknown;
  review_due_at: unknown;
  clause: string;
  last_update: unknown;
  inference_excluded_at: unknown;
  inference_reasons: ExclusionReason[];
}

export interface ClinicalEligibilityView extends ClinicalEligibility {
  patient_id: string;
  programme: string;
  /** True when a stored ELIGIBLE verdict's `review_due_at` has passed. eGFR moves; consent to titrate does not outlive it. */
  expired: boolean;
}

/** Human wording for each exclusion, for the one place a clinician reads it. */
export const EXCLUSION_REASON_LABELS: Record<ExclusionReason, string> = {
  [ExclusionReason.LVAD]: 'LVAD or heart assist device',
  [ExclusionReason.ESRD]: 'End-stage renal disease',
  [ExclusionReason.EGFR_LT_30]: 'eGFR below 30',
};

/**
 * May Welby-driven diuretic titration be offered for this patient?
 *
 * FAILS CLOSED on every input it does not understand — a missing record, an unknown status, an
 * expired verdict. Only an explicit, unexpired `eligible` returns true. `null`/`undefined` is the
 * common case rather than an edge one: it is what a failed read and a never-screened patient both
 * produce, and both must gate the same way.
 */
export const mayTitrate = (view: ClinicalEligibilityView | null | undefined): boolean =>
  view?.status === EligibilityStatus.ELIGIBLE && view.expired !== true;
